import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import makeStyles from "@mui/styles/makeStyles";
import { Alert, Box, Button, CircularProgress, Grid, Link, Paper, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";

import MapContainer from "../../components/MapContainer";
import AdvancedMarker from "../../components/AdvancedMarker";
import { TextItem } from "../../components/TextItem";
import EnvironmentOverlay from "../../components/environment/EnvironmentOverlay";
import EnvironmentLegend from "../../components/environment/EnvironmentLegend";
import MetricButtons from "../../components/environment/MetricButtons";
import { get, USERS } from "../../services/Client";
import { useGetSessionInfo } from "../../services/ContentManager";
import { partialType } from "../../constants/partialType";
import {
    airMetricConfig,
    airMetricOrder,
    NO_DATA_COLOR,
    getMetricColor,
    normalizeMetricValue,
    hexToRgb,
    lerpRgb,
} from "../../constants/airQuality";
import startIcon from "../../images/icons/pin-start.svg";
import finishIcon from "../../images/icons/pin-stop.svg";

const useStyles = makeStyles(theme => ({
    page: {
        padding: theme.spacing(2),
    },
    container: {
        marginBottom: theme.spacing(2),
    },
    mapContainer: {
        width: "100%",
        height: "70vh",
        position: "relative",
    },
    backButton: {
        color: theme.palette.primary.main,
    },
    backButtonIcon: {
        margin: theme.spacing(1),
    },
}));

/**
 * Per ogni metrica, da quale serie della sessione si legge e con quale campo.
 * `aqi_epa` legge `aqiEpa` come fanno gli Open Data (UgoService aggrega aqiEpa sotto quel nome).
 */
const METRIC_SOURCE = {
    ozone_density: { series: "oaq", field: "ozoneDensity" },
    aqi_epa: { series: "oaq", field: "aqiEpa" },
    pm1: { series: "pmx", field: "pm1Density" },
    pm2_5: { series: "pmx", field: "pm2Dot5Density" },
    pm10: { series: "pmx", field: "pm10Density" },
};

/**
 * Campioni validi di una metrica, ordinati nel tempo.
 * Scarta i valori nulli o a zero: lo zero è il sensore non ancora a regime,
 * lo stesso criterio usato dal backend per gli Open Data.
 */
function extractSamples(session, metricKey) {
    const source = METRIC_SOURCE[metricKey];
    const rows = (session && session[source.series]) || [];
    return rows
        .map(r => ({ timestamp: r.timestamp, value: normalizeMetricValue(metricKey, r[source.field]) }))
        .filter(s => s.timestamp != null && s.value != null && s.value > 0)
        .sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Partial con posizione, ordinati nel tempo, divisi in tratti continui:
 * un nuovo tratto parte a ogni START/RESUME, così fra una pausa e la ripresa
 * non si disegna un segmento.
 */
function buildTracks(partials) {
    const points = (partials || [])
        .filter(p => p.latitude && p.longitude && p.timestamp != null && p.valid !== false)
        .sort((a, b) => a.timestamp - b.timestamp);
    const tracks = [];
    let current = [];
    points.forEach(p => {
        const starts = p.type === partialType.START || p.type === partialType.RESUME;
        if (starts && current.length > 0) {
            tracks.push(current);
            current = [];
        }
        current.push(p);
    });
    if (current.length > 0) tracks.push(current);
    return { points, tracks };
}

/** Indice dell'ultimo campione con timestamp <= t (ricerca binaria); -1 se t precede tutti. */
function lastIndexBefore(samples, t) {
    let lo = 0;
    let hi = samples.length - 1;
    let found = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (samples[mid].timestamp <= t) {
            found = mid;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return found;
}

/**
 * Colore della polyline all'istante t: il colore di fascia del campione precedente
 * sfuma linearmente verso quello del campione successivo. Prima del primo campione
 * e dopo l'ultimo vale il colore del campione più vicino.
 */
function colorAt(samples, sampleColors, t) {
    if (samples.length === 0) return hexToRgb(NO_DATA_COLOR);
    const i = lastIndexBefore(samples, t);
    if (i < 0) return sampleColors[0];
    if (i >= samples.length - 1) return sampleColors[samples.length - 1];
    const a = samples[i];
    const b = samples[i + 1];
    const span = b.timestamp - a.timestamp;
    const k = span > 0 ? (t - a.timestamp) / span : 0;
    return lerpRgb(sampleColors[i], sampleColors[i + 1], k);
}

/**
 * Posizione di un campione: il partial più vicino nel tempo che lo precede
 * (lo stesso criterio di UgoService.pickPartialIndex), oppure il primo partial
 * se il campione precede tutti.
 */
function locateSamples(samples, points) {
    if (points.length === 0) return [];
    return samples.map(s => {
        const i = lastIndexBefore(points, s.timestamp);
        const p = points[Math.max(0, i)];
        return { ...s, lat: p.latitude, lng: p.longitude };
    });
}

export default function SessionEnvironment() {
    const classes = useStyles();
    const { id } = useParams();
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const { session, status } = useGetSessionInfo(id);
    const [username, setUsername] = useState();
    const [selectedMetricKey, setSelectedMetricKey] = useState(airMetricOrder[0]);

    useEffect(() => {
        if (session?.uid) get(USERS, { elem: session.uid }).then(u => setUsername(u.username)).catch(() => {});
    }, [session?.uid]);

    const samplesByMetric = useMemo(() => {
        const out = {};
        airMetricOrder.forEach(k => {
            out[k] = session ? extractSamples(session, k) : [];
        });
        return out;
    }, [session]);

    const { tracks } = useMemo(() => buildTracks(session?.partials), [session?.partials]);

    const availableMetrics = airMetricOrder.filter(k => samplesByMetric[k].length > 0);

    // Se la metrica scelta non ha campioni, passa alla prima che ne ha.
    useEffect(() => {
        if (availableMetrics.length > 0 && !availableMetrics.includes(selectedMetricKey)) {
            setSelectedMetricKey(availableMetrics[0]);
        }
    }, [availableMetrics, selectedMetricKey]);

    const getDateWithTimezone = (timestamp, timezone) => {
        const date = dayjs.tz(new Date(timestamp), timezone).format("HH:mm:ss DD/MM/YYYY");
        return timezone ? `${date}  (${timezone})` : date;
    };

    // Il tipo di sessione non conta: dal 27/10/2026 i campioni ambientali arrivano anche dall'app
    // (POST app/sessions) con qualunque tipo di tratta, non solo dal pedale. Conta che ci siano.
    const hasTrack = tracks.length > 0 && tracks.some(tr => tr.length > 1);

    let error = null;
    if (session && availableMetrics.length === 0) {
        error = t("environmentalDataNoSamples", "La sessione non contiene campioni di qualità dell'aria utilizzabili.");
    } else if (session && !hasTrack) {
        error = t("environmentalDataNoTrack", "La sessione non ha un tracciato GPS con cui posizionare le campionature.");
    }

    const activeConfig = airMetricConfig[selectedMetricKey];
    const metricLabel = t(activeConfig.titleTranslationKey);
    const unit = activeConfig.symbol;
    // Campioni della grandezza scelta, con gli orari così come sono: dal 29/10/2026 il backend legge le
    // date dell'app nel fuso del telefono, quindi partial e campioni sono sulla stessa linea del tempo
    // (il pregresso è stato riallineato a DB). Qui non si sposta più nulla: se un campione cade fuori
    // dal tempo del tracciato lo si dice e lo si mostra sul punto di partenza o di arrivo.
    const samples = samplesByMetric[selectedMetricKey] || [];
    const outOfTrack = useMemo(() => {
        const points = tracks.flat();
        if (points.length === 0) return 0;
        const first = points[0].timestamp;
        const last = points[points.length - 1].timestamp;
        return samples.filter(s => s.timestamp < first || s.timestamp > last).length;
    }, [samples, tracks]);

    const countByMetric = useMemo(() => {
        const out = {};
        airMetricOrder.forEach(k => {
            out[k] = samplesByMetric[k].length;
        });
        return out;
    }, [samplesByMetric]);

    // Colore di fascia di ogni campione; la polyline sfuma nel tempo fra un campione e il successivo.
    const sampleColors = useMemo(
        () => samples.map(s => hexToRgb(getMetricColor(selectedMetricKey, s.value))),
        [samples, selectedMetricKey]
    );

    const segments = useMemo(() => {
        const out = [];
        tracks.forEach(track => {
            for (let i = 0; i < track.length - 1; i++) {
                const a = track[i];
                const b = track[i + 1];
                out.push({
                    path: [[a.longitude, a.latitude], [b.longitude, b.latitude]],
                    color: colorAt(samples, sampleColors, a.timestamp),
                });
            }
        });
        return out;
    }, [tracks, samples, sampleColors]);

    const dots = useMemo(() => {
        const points = tracks.flat();
        return locateSamples(samples, points).map((s, i) => ({ ...s, color: sampleColors[i] }));
    }, [tracks, samples, sampleColors]);

    const bounds = useMemo(() => tracks.flat().map(p => ({ lat: p.latitude, lng: p.longitude })), [tracks]);


    const getTooltipText = dot => {
        if (dot.value == null) return null;
        const value = Number(dot.value).toLocaleString(i18n.language, { maximumFractionDigits: 2 });
        return `${metricLabel}: ${value}${unit ? " " + unit : ""}\n${dayjs(dot.timestamp).format("HH:mm:ss")}`;
    };

    const center = useMemo(() => {
        const first = tracks[0] && tracks[0][0];
        return first ? { lat: first.latitude, lng: first.longitude } : undefined;
    }, [tracks]);
    const startPoint = tracks[0] && tracks[0][0];
    const lastTrack = tracks[tracks.length - 1];
    const endPoint = lastTrack && lastTrack[lastTrack.length - 1];

    return (
        <Paper className={classes.page}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Dati ambientali sessione | Pin Bike</title>
            </Helmet>

            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Button onClick={() => history.push(`/sessions/${id}`)} className={classes.backButton}>
                        <ArrowBackIcon className={classes.backButtonIcon} />
                        {t("goBack")}
                    </Button>
                </Grid>
                <Grid item>
                    <Typography variant="h6">{t("environmentalData", "Dati ambientali lungo il tragitto")}</Typography>
                </Grid>
            </Grid>

            <Grid container alignItems="flex-end" className={classes.container}>
                <TextItem value={session && session.id} label={t("sessionID")} />
                <TextItem
                    value={session && <Link href={"/profile/" + session.uid} target="_blank" underline="none">{username}</Link>}
                    label={t("username")}
                />
                <TextItem value={session && getDateWithTimezone(session.startTime, session.timezone)} label={t("startDate")} />
                <TextItem value={session && getDateWithTimezone(session.endTime, session.timezone)} label={t("endDate")} />
                <TextItem value={session && session.gpsDistance ? session.gpsDistance + " Km" : ""} label={t("gpsDistance")} />
            </Grid>

            {status === "loading" && (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" variant="outlined" sx={{ my: 2 }}>
                    {error}
                </Alert>
            )}
            {!error && outOfTrack > 0 && (
                <Alert severity="warning" variant="outlined" sx={{ my: 2 }}>
                    {t("environmentalDataSamplesOutOfTrack", "{{count}} campioni su {{total}} sono fuori dall'intervallo di tempo del tracciato e vengono mostrati sul punto di partenza o di arrivo: gli orari dei campioni e dei partial non sono coerenti (probabile fuso orario sbagliato nell'app).", { count: outOfTrack, total: samples.length })}
                </Alert>
            )}

            {session && !error && (
                <>
                    <div className={classes.container}>
                        <MetricButtons
                            selectedMetricKey={selectedMetricKey}
                            onSelect={setSelectedMetricKey}
                            countByMetric={countByMetric}
                            missingTitle={t("environmentalDataMetricMissing", "Nessuna campionatura di questa grandezza nella sessione")}
                        />
                    </div>

                    <div className={classes.mapContainer}>
                        <MapContainer center={center} zoom={14}>
                            <EnvironmentOverlay segments={segments} dots={dots} bounds={bounds} getTooltipText={getTooltipText} />
                            {startPoint && (
                                <AdvancedMarker
                                    position={{ lat: startPoint.latitude, lng: startPoint.longitude }}
                                    icon={{ url: startIcon, scaledSize: { width: 32, height: 32 } }}
                                />
                            )}
                            {endPoint && (
                                <AdvancedMarker
                                    position={{ lat: endPoint.latitude, lng: endPoint.longitude }}
                                    icon={{ url: finishIcon, scaledSize: { width: 32, height: 32 } }}
                                />
                            )}
                        </MapContainer>
                        <EnvironmentLegend metricKey={selectedMetricKey} metricLabel={metricLabel} unit={unit} />
                    </div>
                </>
            )}
        </Paper>
    );
}
